/**
 * El bloque de carga del detalle de una parada.
 *
 * NO copia medidas a mano: se monta DENTRO de los contenedores reales
 * —`.stop-metric`, `.stop-map`, `.stop-activity-card`— y sólo reemplaza el
 * contenido por barras, con el texto que va a ocupar cada lugar puesto en
 * transparente. El alto entonces lo da el mismo CSS que la vista cargada y no
 * hay dos juegos de medidas que se desincronicen: una barra de 11px donde va un
 * label con `line-height: 1.5715` deja 5px de deuda por línea, y esas deudas
 * son el salto que este componente viene a evitar.
 *
 * Los bloques CONDICIONALES no se adivinan, se reciben. La fila que abre el
 * modal ya sabe si la parada está pendiente y si se cerró por excepción, porque
 * `/route-report` publica `status` y `bypass` de cada parada. Sin ese dato el
 * bloque de carga sobra 147px en una parada pendiente —su mapa vacío mide 120 y
 * el cargado 314— y falta el alto de la alerta en una cerrada por excepción.
 */

/**
 * Lo que la grilla ya sabe de la parada antes de pedir su detalle.
 *
 * Sale de `routes[].stops[]` y de la fila de la ruta que la contiene, o sea de
 * la misma lectura que dibujó la fila desde la que se abrió el modal. Alcanza
 * para pintar el encabezado ENTERO sin esperar nada: el nombre del local, su
 * código, la ruta, el distribuidor y el estado ya están en pantalla cuando el
 * usuario hace click.
 */
export interface StopDetailHint {
  sequence: number;
  code: string;
  label: string | null;
  status: string;
  hasBypass: boolean;
  hasBypassNote: boolean;
  routeCode: string;
  distributorName: string | null;
}

interface SkeletonLineProps {
  className: string;
  width: number | string;
  /** Sólo importa por su alto de línea: nunca se ve. */
  text: string;
}

function SkeletonLine({ className, width, text }: SkeletonLineProps) {
  return (
    <div
      className={`${className} stop-sk`}
      /* El ancho es el que tendría el texto en un modal holgado, pero nunca
         puede pasarse del contenedor: en una pantalla angosta una barra de
         245px dentro de una card de 295px sacaba scroll horizontal. */
      style={{ width, maxWidth: '100%', color: 'transparent' }}
      aria-hidden="true"
    >
      {text}
    </div>
  );
}

interface StopDetailSkeletonProps {
  hint?: StopDetailHint;
}

function StopDetailSkeleton({ hint }: StopDetailSkeletonProps) {
  const pending = hint?.status === 'pending';

  const metric = (key: string) => (
    <div className="stop-metric" key={key}>
      <SkeletonLine className="stop-metric__label" width={52} text="Inicio" />
      <SkeletonLine className="stop-metric__value" width={76} text="00:00" />
      <SkeletonLine
        className="stop-metric__sub"
        width={92}
        text="00 mmm 0000"
      />
    </div>
  );

  return (
    <div className="stop-detail">
      <div className="stop-detail__metrics">
        {['a', 'b', 'c', 'd'].map(metric)}
      </div>

      {hint?.hasBypass && (
        <div className="stop-section">
          <div className="stop-alert">
            <span className="stop-sk stop-sk--icon" aria-hidden="true" />
            <span className="stop-alert__body">
              <SkeletonLine
                className="stop-alert__line"
                width="62%"
                text="Motivo"
              />
              {hint.hasBypassNote && (
                <SkeletonLine
                  className="stop-alert__note"
                  width="78%"
                  text="Comentario"
                />
              )}
            </span>
          </div>
        </div>
      )}

      <div className="stop-section">
        <SkeletonLine
          className="stop-section__title"
          width={168}
          text="Ubicación"
        />
        <div className="stop-map">
          {/* Una parada pendiente no tiene coordenadas: su mapa es el bloque
              vacío, no el lienzo con su pie de coordenadas. */}
          {pending ? (
            <div className="stop-map__empty" />
          ) : (
            <span className="stop-sk stop-map__canvas-sk" aria-hidden="true" />
          )}

          <div className="stop-map__legend">
            {[148, 96, 84].map((width) => (
              <span
                className="stop-sk stop-sk--pill"
                style={{ width, maxWidth: '100%' }}
                key={width}
                aria-hidden="true"
              />
            ))}
          </div>

          {/* El tag de precisión va porque `accuracyM` viene en toda parada
              visitada: es lo que lleva el pie de 38 a 41px, y omitirlo deja
              justamente esos 3px de salto. */}
          {!pending && (
            <div className="stop-map__foot">
              <SkeletonLine
                className="stop-map__foot-line"
                width={205}
                text="-00.0000000, -00.0000000"
              />
              <span className="stop-sk stop-sk--tag" aria-hidden="true" />
            </div>
          )}
        </div>
      </div>

      <div className="stop-section">
        <SkeletonLine
          className="stop-section__title"
          width={88}
          text="Actividades"
        />
        {[
          { name: 158, meta: 245 },
          { name: 172, meta: 198 },
        ].map((widths) => (
          <div className="stop-activity-card" key={widths.name}>
            <span className="stop-sk stop-sk--avatar" aria-hidden="true" />
            <span className="stop-activity-card__body">
              <SkeletonLine
                className="stop-activity-card__name"
                width={widths.name}
                text="Actividad"
              />
              <SkeletonLine
                className="stop-activity-card__meta"
                width={widths.meta}
                text="Estado de la actividad"
              />
            </span>
            <span className="stop-sk stop-sk--tag" aria-hidden="true" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default StopDetailSkeleton;
